import React, { useEffect, useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { useSelector } from 'react-redux';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CommanServices } from '@/services/commanServices';

const RecentIncidents = ({ isCollapsed, onNavigate }) => {
  const { recentIncident } = useSelector((state) => state.organization);
  const [onRecentIncidents, setOnRecentIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Just now';
    const now = new Date();
    const openedAt = dateString.endsWith('Z') ? new Date(dateString) : new Date(`${dateString}Z`);
    const diffMs = now - openedAt;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMinutes > 0) return `${diffMinutes}m ago`;
    return 'Just now';
  };

  useEffect(() => {
    getIncidentHistory();
  }, []);

  useEffect(() => {
    if (recentIncident && recentIncident?.id) {
      setOnRecentIncidents((prev) => {
        const exists = prev.some((incident) => incident.id === recentIncident.id);
        const updated = exists ? prev : [recentIncident, ...prev];

        // Sort by updated_at (newest first)
        return [...updated].sort(
          (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
        );
      });
    }
  }, [recentIncident]);

  const getIncidentHistory = async () => {
    setLoading(true);
    try {
      const res = await CommanServices.getIncidentHistory();
      if (res.status) {
        const sortedData = (res?.data || []).sort(
          (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
        );
        setOnRecentIncidents(sortedData);
      }
    } catch (err) {
      setOnRecentIncidents([]);
      console.error('Failed to load Post Incident Reports', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteIncidentHistory = async (id) => {
    setLoading(true);
    try {
      await CommanServices.deleteIncidentHistory(id);
    } catch (err) {
      console.error('Failed to delete incident', err);
    } finally {
      setLoading(false);
    }
  };

  if (!onRecentIncidents || onRecentIncidents.length === 0) {
    return null;
  }

  return (
    <div className={`mt-4 pt-4 border-t border-gray-200 ${isCollapsed ? 'hidden' : ''}`}>
      <div className='px-3 mb-2'>
        <h3 className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>Recent Incidents</h3>
      </div>
      <div className="space-y-1">
        {onRecentIncidents?.map((incident, index) => (
          <TooltipProvider key={incident.id}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="group relative px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                  onClick={() => {
                    onNavigate?.();
                    navigate(`/incident-details/${incident.id}`);
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                      <div
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          incident?.severity?.toLowerCase() === 'high'
                            ? 'bg-red-500'
                            : incident?.severity?.toLowerCase() === 'medium'
                            ? 'bg-orange-500'
                            : 'bg-yellow-500'
                        }`}
                      />
                      <span className="text-xs text-gray-700 truncate">
                        {incident?.type} — {incident?.shortLocationLabel}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteIncidentHistory(incident.id);
                        setOnRecentIncidents((prev) =>
                          prev.filter((_, i) => i !== index)
                        );
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-opacity flex-shrink-0"
                      aria-label="Remove incident"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-full">
                <div className="text-xs">
                  {incident?.fullLocation} • {incident?.id} •{' '}
                  {getTimeAgo(incident?.updated_at)}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
      </div>
    </div>
  );
};

export default RecentIncidents;
